import { useState, useEffect, useContext, useCallback, useMemo } from 'react'
import { useAuthContext } from 'Context/AuthContext/AuthContext'
import { StorageContext } from 'Context'
import { checkCanSubmitToday } from 'Utils/AllStar/allStarHelper'
import {
  getUserId,
  getAuthStateDebug,
  normalizeError,
  logEvent,
  getSearchParams,
} from 'Utils'
import { STORAGE_NAMES } from '../../../../Constant/storage'
import { ANALYTICS_TAGS } from '../../../../Constant'
import { retrieveFromCache } from '../../../../Utils/windowHelpers/storageHelpers'

export const useURLQuery = () => {
  return useMemo(() => getSearchParams() || new URLSearchParams(), [])
}

export const useEmail = () => {
  const query = useURLQuery()
  const { email: em, authLoading, isLoggedIn, userId } = useAuthContext()
  const [email, setEmail] = useState<string>('')
  const emailFromQuery = query.get('email')

  useEffect(() => {
    if (emailFromQuery) {
      setEmail(emailFromQuery)
    } else if (em) {
      setEmail(em)
    }
  }, [em, emailFromQuery, authLoading, userId, isLoggedIn])

  return { email, setEmail }
}

export const useVoteStatus = () => {
  const authContext = useAuthContext()
  const { authLoading } = authContext
  const { isStorageReady, storageStatuses } = useContext(StorageContext)!
  const [canVote, setCanVote] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [retryCount, setRetryCount] = useState<number>(0)

  const getLastVoteDate = useCallback(async () => {
    if (!isStorageReady) return null

    try {
      const result = await retrieveFromCache(
        STORAGE_NAMES.allStarVoteDate,
        storageStatuses
      )
      return result?.data ?? null
    } catch (error) {
      console.error('Error retrieving last vote date from storage:', error)
      const userId = getUserId(authContext)
      logEvent(ANALYTICS_TAGS.allstar_vote_status_error, {
        error: 'storage_retrieval_exception',
        userId: userId || 'anonymous',
        errorMessage: normalizeError(error),
      })
      return null
    }
  }, [isStorageReady, storageStatuses, authContext])

  useEffect(() => {
    checkVoteStatus()
  }, [isStorageReady, authLoading, getLastVoteDate, authContext, retryCount])

  const checkVoteStatus = async () => {
    if (!isStorageReady || authLoading) {
      if (isStorageReady && authLoading && retryCount < 3) {
        // If storage is ready but auth is still loading, wait a bit and retry
        setTimeout(() => {
          setRetryCount((prev) => prev + 1)
        }, 500)
      }
      return
    }

    setIsLoading(true)
    const userId = getUserId(authContext)

    try {
      const lastVoteDate = await getLastVoteDate()
      const canSubmitToday = lastVoteDate ? checkCanSubmitToday(lastVoteDate) : true

      logEvent(ANALYTICS_TAGS.allstar_vote_status_check, {
        canVote: canSubmitToday,
        hasLastVoteDate: !!lastVoteDate,
        lastVoteDate: lastVoteDate || null,
        userId: userId || 'anonymous',
        authState: getAuthStateDebug(authContext, authLoading),
        retryCount,
      })

      if (!canSubmitToday) {
        logEvent(ANALYTICS_TAGS.allstar_vote_button_locked, {
          reason: 'already_voted_today',
          lastVoteDate: lastVoteDate,
          userId: userId || 'anonymous',
        })
      }

      setCanVote(canSubmitToday)
      setIsLoading(false)
      setRetryCount(0)
    } catch (error) {
      console.error('Error checking vote status:', error)
      logEvent(ANALYTICS_TAGS.allstar_vote_status_error, {
        error: 'vote_status_check_failed',
        userId: userId || 'anonymous',
        authState: getAuthStateDebug(authContext, authLoading),
        retryCount,
      })

      setCanVote(true)
      setIsLoading(false)
    }
  }

  return { canVote, setCanVote, isLoading }
}
